<template>
  <div
    id="dev-PixRegistrationForm"
    class="pix-registration-form"
  >
    <div class="form-body mt-3">
      <div class="form-group">
        <BaseInput
          id="pix-name"
          v-model="pixData.name"
          type="text"
          :label="$t('pix_registration.full_name')"
          :error="errors.name"
          :placeholder="$t('pix_registration.full_name')"
          variant="success"
        />
      </div>

      <div class="form-group">
        <BaseInput
          id="pix-cpf"
          v-model="pixData.cpfCnpj"
          :mask="cpfCnpjMask"
          type="tel"
          :label="'CPF/CNPJ'"
          :error="errors.cpfCnpj"
          placeholder="CPF/CNPJ"
          variant="success"
        />
      </div>

      <div
        v-if="errorMessage"
        class="alert alert-danger"
        role="alert"
      >
        {{ errorMessage }}
      </div>
    </div>

    <div class="form-actions">
      <BaseButton
        variant="primary"
        size="sm"
        block
        constrained
        :disabled="loading"
        @click="registerPix"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ loading ? $t('common.saving') : $t('pix_registration.button') }}
      </BaseButton>

      <BaseButton
        variant="outline-secondary"
        size="sm"
        block
        constrained
        class="mt-2"
        :disabled="loading"
        @click="$emit('cancel')"
      >
        {{ $t('common.cancel') }}
      </BaseButton>
    </div>

    <!-- Trust badges -->
    <div class="trust-badges">
      <div class="badge-item">
        <font-awesome-icon icon="lock" />
        <span>{{ $t('common.secure') }}</span>
      </div>
      <div class="badge-item">
        <font-awesome-icon icon="shield-alt" />
        <span>{{ $t('common.protected') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
  import { cpf, cnpj } from 'cpf-cnpj-validator'
  import createPixIdentification from '@/views/home/service/createPixIdentification'
  import handleError from '@/services/handleError'
  import { sessionRecorder, UserEvent } from '@/services/sessionRecorder'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import BaseInput from '@/components/ui/BaseInput.vue'

  const TAG_PIX_REGISTRATION = 'pix_registration_inline'

  export default {
    name: 'PixRegistrationForm',
    components: {
      BaseButton,
      BaseInput
    },
    props: {
      user: {
        type: Object,
        required: true
      }
    },
    emits: ['payment-added', 'cancel'],
    data() {
      return {
        pixData: {
          name: '',
          cpfCnpj: ''
        },
        errors: {
          name: null,
          cpfCnpj: null
        },
        errorMessage: '',
        loading: false
      }
    },
    computed: {
      cpfCnpjMask() {
        if (this.pixData.cpfCnpj.replace(/\D/g, '').length <= 11) {
          return '###.###.###-##'
        } else {
          return '##.###.###/####-##'
        }
      }
    },
    watch: {
      pixData: {
        handler() {
          // Clear errors when user types
          this.errors.name = null
          this.errors.cpfCnpj = null
          this.errorMessage = ''
        },
        deep: true
      }
    },
    mounted() {
      sessionRecorder.addUserEvent(new UserEvent(TAG_PIX_REGISTRATION))
    },
    methods: {
      validatePixData() {
        let isValid = true
        this.errors = { name: null, cpfCnpj: null }
        this.errorMessage = ''

        // Validate name
        if (this.pixData.name === '') {
          this.errors.name = this.$t('pix_registration.errors.name_required')
          isValid = false
        } else if (this.pixData.name.split(' ').length < 2) {
          this.errors.name = this.$t('pix_registration.errors.name_complete')
          isValid = false
        }

        // Validate CPF/CNPJ
        const cpfCnpjClean = this.pixData.cpfCnpj.replace(/\D/g, '')

        if (cpfCnpjClean.length === 0) {
          this.errors.cpfCnpj = this.$t('pix_registration.errors.cpf_cnpj_required')
          isValid = false
        } else if (cpfCnpjClean.length === 11) {
          if (!cpf.isValid(cpfCnpjClean)) {
            this.errors.cpfCnpj = this.$t('pix_registration.errors.cpf_invalid')
            isValid = false
          }
        } else if (cpfCnpjClean.length === 14) {
          if (!cnpj.isValid(cpfCnpjClean)) {
            this.errors.cpfCnpj = this.$t('pix_registration.errors.cnpj_invalid')
            isValid = false
          }
        } else {
          this.errors.cpfCnpj = this.$t('pix_registration.errors.cpf_cnpj_invalid_length')
          isValid = false
        }

        return isValid
      },

      async registerPix() {
        if (!this.validatePixData()) {
          return
        }

        this.loading = true
        const cpfCnpjClean = this.pixData.cpfCnpj.replace(/\D/g, '')

        try {
          const user = await createPixIdentification(this.user, {
            name: this.pixData.name,
            cpf_or_cnpj: cpfCnpjClean
          })

          if (user) {
            this.$emit('payment-added', {
              type: 'pix',
              user: user
            })
          } else {
            this.errorMessage = this.$t('pix_registration.errors.register_error')
          }
        } catch (error) {
          this.errorMessage = this.$t('pix_registration.errors.register_error_retry')
          handleError(error)
        } finally {
          this.loading = false
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .pix-registration-form {
    padding: 0;
    background: white;
    border-radius: 8px;
  }

  .form-header {
    text-align: center;
    margin-bottom: 1rem;
  }

  .pix-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
    border-radius: 50%;
    margin-bottom: 0.5rem;
    box-shadow: 0 2px 8px rgba(46, 125, 50, 0.12);
  }

  .pix-icon {
    font-size: 1.25rem;
    color: #2e7d32;
  }

  .form-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.25rem;
  }

  .form-subtitle {
    font-size: 0.75rem;
    color: #666;
    margin: 0;
  }

  .form-body {
    margin-bottom: 1rem;
  }

  .form-group {
    margin-bottom: 0.75rem;
  }

  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 0.375rem;
  }

  .form-control {
    width: 100%;
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
    border: 1.5px solid #e0e0e0;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #2e7d32;
      box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.08);
    }

    &.is-invalid {
      border-color: #dc3545;

      &:focus {
        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.08);
      }
    }

    &::placeholder {
      color: #999;
    }
  }

  .invalid-feedback {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.6875rem;
    color: #dc3545;
  }

  .alert {
    padding: 0.625rem 0.75rem;
    border-radius: 6px;
    font-size: 0.75rem;
    margin-top: 0.75rem;
  }

  .alert-danger {
    color: #842029;
    background-color: #f8d7da;
    border: 1px solid #f5c2c7;
  }

  .form-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    font-size: var(--fs-xs);
    font-weight: var(--fw-medium);
    letter-spacing: -0.01em;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-primary {
    background-color: #f58a48 !important;
    border-color: #f58a48 !important;
    color: white;

    &:hover:not(:disabled) {
      background-color: #e67e3e !important;
      border-color: #e67e3e !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(245, 138, 72, 0.2);
    }
  }

  .btn-outline-secondary {
    background: white;
    color: #6c757d;
    border: 1.5px solid #dee2e6;

    &:hover:not(:disabled) {
      background: #f8f9fa;
      border-color: #adb5bd;
    }
  }

  .btn-block {
    width: 100%;
    max-width: 250px;
  }

  .mt-2 {
    margin-top: 0.5rem;
  }

  .me-2 {
    margin-right: 0.5rem;
  }

  .spinner-border {
    display: inline-block;
    width: 0.875rem;
    height: 0.875rem;
    vertical-align: text-bottom;
    border: 0.125em solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spinner-border 0.75s linear infinite;
  }

  .spinner-border-sm {
    width: 0.75rem;
    height: 0.75rem;
    border-width: 0.1em;
  }

  @keyframes spinner-border {
    to {
      transform: rotate(360deg);
    }
  }

  .trust-badges {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e0e0e0;
  }

  .badge-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.6875rem;
    color: #666;

    svg {
      color: #2e7d32;
      font-size: 0.75rem;
    }
  }

  @media (max-width: 576px) {
    .pix-registration-form {
      padding: 0;
    }

    .form-title {
      font-size: 0.875rem;
    }

    .btn {
      padding: 0.5rem 0.875rem;
      font-size: 0.75rem;
    }
  }
</style>
